import { FormEvent, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiErrorMessage } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import type { Company, CompanyOptions } from '../api/types';

type Form = Partial<Company>;

export default function CompanyPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<Form>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [certPassword, setCertPassword] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: company } = useQuery({
    queryKey: ['company'],
    queryFn: async () => (await api.get<Company>('/company')).data,
  });
  const { data: options } = useQuery({
    queryKey: ['company-options'],
    queryFn: async () => (await api.get<CompanyOptions>('/company/options')).data,
  });

  // Preenche o form quando carrega.
  useEffect(() => {
    if (company) setForm(company);
  }, [company]);

  function set<K extends keyof Form>(k: K, v: Form[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const save = useMutation({
    mutationFn: async () => (await api.put<Company>('/company', form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['company'] }); toast.success('Empresa salva'); },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha ao salvar')),
  });

  const uploadCert = useMutation({
    mutationFn: async () => {
      const file = fileRef.current?.files?.[0];
      if (!file) throw new Error('Selecione o arquivo .pfx');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('password', certPassword);
      return (await api.post('/company/cert', fd)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company'] });
      setCertPassword('');
      if (fileRef.current) fileRef.current.value = '';
      toast.success('Certificado salvo');
    },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha no certificado')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  if (!company || !options) return <div className="text-sm text-slate-500">Carregando…</div>;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Empresa</h2>
      <p className="text-sm text-slate-600">Dados do prestador usados na emissão de NFS-e.</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Identidade */}
        <fieldset className="card space-y-3">
          <legend className="font-semibold text-sm">Identidade</legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <label className="block sm:col-span-2">
              Razão social
              <input className="input" value={form.nome || ''} onChange={(e) => set('nome', e.target.value)} />
            </label>
            <label className="block">
              CNPJ
              <input className="input" value={form.cnpj || ''} onChange={(e) => set('cnpj', e.target.value)} placeholder="só dígitos" />
            </label>
            <label className="block">
              Inscrição Municipal
              <input className="input" value={form.im || ''} onChange={(e) => set('im', e.target.value)} />
            </label>
            <label className="block">
              Simples Nacional
              <select className="input" value={form.op_simp_nac || '3'} onChange={(e) => set('op_simp_nac', e.target.value)}>
                {Object.entries(options.op_simp_nac).map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              Município emissor
              <select className="input" value={form.cod_municipio || '2919207'} onChange={(e) => set('cod_municipio', e.target.value)}>
                {Object.entries(options.municipios).map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              Série da DPS
              <input className="input" value={form.serie || ''} onChange={(e) => set('serie', e.target.value)} placeholder="00001" />
            </label>
          </div>
        </fieldset>

        {/* Certificado */}
        <fieldset className="card space-y-2">
          <legend className="font-semibold text-sm">Certificado A1 (.pfx)</legend>
          {company.has_cert ? (
            <div className="text-xs text-emerald-700 bg-emerald-50 rounded p-2">
              ✓ Certificado configurado{company.cert_validade ? ` · válido até ${company.cert_validade}` : ''}.
              {company.cert_nome && <div className="text-slate-500 mt-1 break-words">{company.cert_nome}</div>}
            </div>
          ) : (
            <div className="text-xs text-amber-700 bg-amber-50 rounded p-2">Nenhum certificado configurado.</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <label className="block">
              Arquivo .pfx {company.has_cert && <span className="text-slate-400">(enviar substitui o atual)</span>}
              <input ref={fileRef} type="file" accept=".pfx,.p12" className="text-sm block mt-1" />
            </label>
            <label className="block">
              Senha do certificado
              <input className="input" type="password" value={certPassword} onChange={(e) => setCertPassword(e.target.value)} autoComplete="off" />
            </label>
          </div>
          <button type="button" className="btn btn-secondary text-sm" disabled={uploadCert.isPending} onClick={() => uploadCert.mutate()}>
            {uploadCert.isPending ? 'Enviando…' : 'Salvar certificado'}
          </button>
        </fieldset>

        {/* Ambiente */}
        <fieldset className="card space-y-2">
          <legend className="font-semibold text-sm">Ambiente</legend>
          {company.is_admin ? (
            <>
              <label className="block text-sm">
                Ambiente de emissão
                <select className="input" value={form.ambiente || company.env_ambiente} onChange={(e) => set('ambiente', e.target.value)}>
                  {Object.entries(options.ambientes).map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </select>
              </label>
              {(form.ambiente || company.env_ambiente) === 'producao' && (
                <div className="text-xs text-red-700 bg-red-50 rounded p-2">
                  ⚠️ Produção emite notas fiscais REAIS e irreversíveis.
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-slate-600">
              Ambiente atual: <strong>{company.env_ambiente}</strong> (definido pelo servidor; só o admin pode alterar).
            </div>
          )}
        </fieldset>

        {/* Avançado */}
        <fieldset className="card space-y-2">
          <button type="button" className="font-semibold text-sm text-brand-600" onClick={() => setShowAdvanced((v) => !v)}>
            {showAdvanced ? '▾' : '▸'} Avançado (serviço e exportação)
          </button>
          {showAdvanced && (
            <div className="space-y-3 pt-2">
              <div className="text-xs text-slate-500 bg-slate-100 rounded p-2">
                Consulte os códigos oficiais (Tributação Nacional / LC 116 e NBS) em{' '}
                <a
                  className="text-brand-600 underline"
                  href="https://www.gov.br/nfse/pt-br/mei-e-demais-empresas/codigos-de-tributacao-nacional-nbs"
                  target="_blank"
                  rel="noreferrer"
                >
                  portal nacional da NFS-e ↗
                </a>
                .
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <label className="block">
                  Item lista serviço (cTribNac)
                  <input className="input" value={form.item_lista_servico || ''} onChange={(e) => set('item_lista_servico', e.target.value)} placeholder="010101 (Análise e desenvolvimento de sistemas)" />
                </label>
                <label className="block">
                  Cód. trib. municipal (cTribMun)
                  <input className="input" value={form.cod_atividade || ''} onChange={(e) => set('cod_atividade', e.target.value)} placeholder="3 dígitos — opcional, deixe vazio se não souber" />
                </label>
                <label className="block">
                  NBS (Nomenclatura Brasileira de Serviços)
                  <input className="input" value={form.nbs || ''} onChange={(e) => set('nbs', e.target.value)} placeholder="112019000 (pesquisa e desenvolvimento)" />
                </label>
                <label className="flex items-center gap-2 mt-5">
                  <input type="checkbox" checked={!!form.iss_retido} onChange={(e) => set('iss_retido', e.target.checked)} /> ISS retido na fonte
                </label>
              </div>
              <div className="text-xs font-semibold text-slate-400 uppercase pt-1">Exportação (tomador no exterior)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <label className="block">
                  Moeda
                  <select className="input" value={form.export_moeda || '790'} onChange={(e) => set('export_moeda', e.target.value)}>
                    {Object.entries(options.moedas).map(([v, label]) => (
                      <option key={v} value={v}>{label}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  Modo de prestação
                  <select className="input" value={form.export_modo_prestacao || '1'} onChange={(e) => set('export_modo_prestacao', e.target.value)}>
                    <option value="1">1 — Transfronteiriço</option>
                    <option value="2">2 — Consumo no Brasil</option>
                    <option value="3">3 — Movimento temporário de pessoas físicas</option>
                    <option value="4">4 — Consumo no exterior</option>
                    <option value="0">0 — Não informado</option>
                  </select>
                </label>
              </div>
            </div>
          )}
        </fieldset>

        <button type="submit" className="btn btn-primary" disabled={save.isPending}>
          {save.isPending ? 'Salvando…' : 'Salvar empresa'}
        </button>
      </form>
    </div>
  );
}
